import { Accordion, Spinner } from 'react-bootstrap';
import { SFCAccordion } from '../../../../components/accordion/SFCAccordion';
import { question } from '../../../../types/questions.type';
import SafeHTMLDisplay from '../../../common/SafeHTMLDisplay';
import './FAQAccordion.css';

interface FAQAccordionProps {
  questions: question[];
  isLoading: boolean;
  activeKey?: string | null;
  onSelect?: (eventKey: string | null) => void;
}

/** Normalize API HTML answers and legacy string[] answers into list items when possible. */
const getAnswerItems = (answer: unknown): string[] | null => {
  if (Array.isArray(answer)) {
    return answer.filter((item): item is string => typeof item === 'string');
  }
  if (typeof answer !== 'string' || !answer.trim()) {
    return null;
  }

  // CKEditor list: extract <li> text so we can keep logo-bullet formatting
  const listItemMatches = [...answer.matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi)];
  if (listItemMatches.length > 0) {
    return listItemMatches.map((match) => match[1].trim()).filter(Boolean);
  }

  // Multiple paragraphs from the editor → treat each as a bullet like the old page
  const paragraphMatches = [...answer.matchAll(/<p[^>]*>([\s\S]*?)<\/p>/gi)];
  if (paragraphMatches.length > 1) {
    return paragraphMatches.map((match) => match[1].trim()).filter(Boolean);
  }

  return null;
};

const renderAnswer = (answer: unknown) => {
  const items = getAnswerItems(answer);

  if (items && items.length > 0) {
    return (
      <ul className="faq-list-unstyled">
        {items.map((item, index) => (
          <li
            key={index}
            className="faq-list-item"
          >
            <SafeHTMLDisplay htmlContent={item} />
          </li>
        ))}
      </ul>
    );
  }

  if (typeof answer === 'string' && answer.trim()) {
    return (
      <div className="faq-answer-html">
        <SafeHTMLDisplay htmlContent={answer} />
      </div>
    );
  }

  return null;
};

export const FAQAccordion = ({ questions, isLoading, activeKey, onSelect }: FAQAccordionProps) => {
  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '200px' }}
      >
        <Spinner
          animation="border"
          variant="primary"
        />
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="p-4 text-center">
        <p className="text-muted">No frequently asked questions found for this category.</p>
      </div>
    );
  }

  return (
    <SFCAccordion
      activeKey={activeKey}
      onSelect={onSelect}
    >
      {questions.map((question, index) => (
        <Accordion.Item
          eventKey={index.toString()}
          key={question.id}
        >
          <Accordion.Header>{question.name}</Accordion.Header>
          <Accordion.Body>{renderAnswer(question.answer)}</Accordion.Body>
        </Accordion.Item>
      ))}
    </SFCAccordion>
  );
};
